import { useStore } from '../store/store';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { backupFlow } from '../consts';
import { transitionPages } from '../consts';
import { REDIRECT_RULES } from '../constants/redirectRules';

export function useNav() {
  // @ts-ignore
  const { 
    routeGroups: flow, 
    transitionVisibilityGender,
    dynamicRedirectRules
  } = useRemoteConfig();
  
  const flatFlow = flow?.length > 0 ? flow : backupFlow;
  const quizFlow = flatFlow.flat();
  const pageConfig = [...quizFlow];

  const setPage = useStore((state) => state.setPage);
  const currentPage = useStore((state) => state.currentPage);
  const addVisitedPage = useStore((state) => state.addVisitedPage);
  const currentIndex = pageConfig.indexOf(currentPage);

  const getAllRedirectRules = () => {
    const staticRules = REDIRECT_RULES || [];
    const dynamicRules = dynamicRedirectRules || [];
    return { staticRules, dynamicRules };
  };

  const getNextPageWithRules = () => {
    const currentState = useStore.getState();
    const currentPageValue = currentState.currentPage;
    const pageAnswers = currentState.pageAnswers;
    const currentIndexValue = pageConfig.indexOf(currentPageValue);

    const { staticRules, dynamicRules } = getAllRedirectRules();

    const applicableStaticRule = staticRules.find((rule) => 
      rule.from === currentPageValue && 
      currentState[rule.field] === rule.value
    );

    if (applicableStaticRule) {
      const targetPage = applicableStaticRule.to;
      const targetAllowed = pageConfig.includes(targetPage) && isPageAllowed(targetPage);
      
      if (targetAllowed) {
        return targetPage;
      }
    }

    //@ts-ignore
    const rulesForCurrentPage = dynamicRules.filter(rule => rule.from === currentPageValue);

    for (const rule of rulesForCurrentPage) {
      //@ts-ignore
      const pageAnswer = pageAnswers[rule.field];
      //@ts-ignore
      const conditionMet = pageAnswer === rule.value;
      
      if (conditionMet) {
        //@ts-ignore
        const targetPage = rule.to;
        const targetAllowed = pageConfig.includes(targetPage) && isPageAllowed(targetPage);
        
        if (targetAllowed) {
          return targetPage;
        }
      }
    }

    for (let i = currentIndexValue + 1; i < pageConfig.length; i++) {
      const page = pageConfig[i];
      const allowed = isPageAllowed(page);

      if (allowed) {
        return page;
      }
    }

    return null;
  };

  const isPageVisible = (page: string) => {
    const currentState = useStore.getState();
    const gender = currentState.gender;

    if (transitionPages.includes(page)) {
      if (transitionVisibilityGender === 'male-only' && gender === 'Male') return true;
      if (transitionVisibilityGender === 'female-only' && gender === 'Female') return true;
      if (transitionVisibilityGender === 'all') return true;
      return false;
    }

    return true;
  };

  const isPageAllowed = (page: string) => {
    const currentState = useStore.getState();
    return isPageVisible(page) && !currentState.skippedPages.includes(page);
  };

  const getPreviousPage = () => {
    const currentState = useStore.getState();
    const currentVisitedPages = currentState.visitedPages;
    const currentPageValue = currentState.currentPage;

    if (currentVisitedPages.length === 0) {
      return null;
    }

    const currentPageIndex = currentVisitedPages.indexOf(currentPageValue);

    if (currentPageIndex === -1) {
      return currentVisitedPages.length > 0
        ? currentVisitedPages[currentVisitedPages.length - 1]
        : null;
    }

    if (currentPageIndex > 0) {
      return currentVisitedPages[currentPageIndex - 1];
    }

    return null;
  };

  const onPrev = () => {
    const prevPage = getPreviousPage();
    if (prevPage) {
      setPage(prevPage);
    }
  };

  const onNext = () => {
    const nextPage = getNextPageWithRules();

    if (nextPage) {
      const currentPageValue = useStore.getState().currentPage;
      addVisitedPage(currentPageValue);
      setPage(nextPage);
    }
  };

  return { onPrev, onNext, currentIndex };
}